import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Teams from "@/pages/Teams";
import Players from "@/pages/Players";
import PlayerDashboard from "@/pages/PlayerDashboard";
import PlayerStatDetail from "@/pages/PlayerStatDetail";
import TeamStatDetail from "@/pages/TeamStatDetail";
import RivalPlayerDashboard from "@/pages/RivalPlayerDashboard";
import Trainings from "@/pages/Trainings";
import TrainingDetail from "@/pages/TrainingDetail";
import Matches from "@/pages/Matches";
import MatchDetail from "@/pages/MatchDetail";
import Coaches from "@/pages/Coaches";
import Admins from "@/pages/Admins";
import Rivals from "@/pages/Rivals";
import RffmEngine from "@/pages/RffmEngine";
import RffmSync from "@/pages/RffmSync";
import RivalTeamDashboard from "@/pages/RivalTeamDashboard";
import RivalDatasetPlayer from "@/pages/RivalDatasetPlayer";
import Scouting from "@/pages/ScoutingMarket";
import ScoutingPlayerDetail from "@/pages/ScoutingPlayerDetail";
import PhysicalTests from "@/pages/PhysicalTests";
import LoadControl from "@/pages/LoadControl";
import CoordinatorKPIs from "@/pages/CoordinatorKPIs";
import SeasonMigration from "@/pages/SeasonMigration";
import CoachEvaluations from "@/pages/CoachEvaluations";
import TaskLibrary from "@/pages/TaskLibrary";
import Calendar from "@/pages/Calendar";
import TrainingSchedules from "@/pages/TrainingSchedules";
import DemoAccess from "@/pages/DemoAccess";
import Physio from "@/pages/Physio";
import VersionGuard from "@/components/VersionGuard";
import PWAInstallBanner from "@/components/PWAInstallBanner";

const withLayout = (Page, props = {}) => (
  <ProtectedRoute>
    <Layout>
      <Page {...props} />
    </Layout>
  </ProtectedRoute>
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <VersionGuard />
          <Toaster richColors position="top-right" />
          <PWAInstallBanner />
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/demo/:token" element={<DemoAccess />} />
          <Route path="/dashboard" element={withLayout(Dashboard)} />
          <Route path="/calendario" element={withLayout(Calendar)} />
          <Route path="/horarios" element={withLayout(TrainingSchedules)} />
          <Route path="/teams" element={withLayout(Teams)} />
          <Route path="/teams/:teamId/fisico" element={withLayout(PhysicalTests)} />
          <Route path="/fisico" element={withLayout(PhysicalTests)} />
          <Route path="/teams/:teamId/cargas" element={withLayout(LoadControl)} />
          <Route path="/cargas" element={withLayout(LoadControl)} />
          <Route path="/coordinador" element={withLayout(CoordinatorKPIs)} />
          <Route
            path="/migracion"
            element={
              <ProtectedRoute adminOnly>
                <Layout><SeasonMigration /></Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/eval-entrenadores" element={withLayout(CoachEvaluations)} />
          <Route path="/tareas" element={withLayout(TaskLibrary)} />
          <Route path="/players" element={withLayout(Players)} />
          <Route path="/players/:playerId/dashboard" element={withLayout(PlayerDashboard)} />
          <Route path="/players/:playerId/detalle-asistencia" element={withLayout(PlayerStatDetail, { mode: "attendance" })} />
          <Route path="/players/:playerId/detalle-partidos" element={withLayout(PlayerStatDetail, { mode: "matches" })} />
          <Route path="/teams/:teamId/detalle-stats" element={withLayout(TeamStatDetail)} />
          <Route path="/trainings" element={withLayout(Trainings)} />
          <Route path="/trainings/:id" element={withLayout(TrainingDetail)} />
          <Route path="/matches" element={withLayout(Matches)} />
          <Route path="/matches/:id" element={withLayout(MatchDetail)} />
          <Route path="/rivals" element={withLayout(Rivals)} />
          <Route path="/rffm" element={withLayout(RffmEngine)} />
          <Route path="/rffm-sync" element={withLayout(RffmSync)} />
          <Route path="/rivals/equipo/:name" element={withLayout(RivalTeamDashboard)} />
          <Route path="/rivals/jugador/:jugadorId" element={withLayout(RivalDatasetPlayer)} />
          <Route
            path="/scouting"
            element={
              <ProtectedRoute adminOnly>
                <Layout><Scouting /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/scouting/player/:id"
            element={
              <ProtectedRoute adminOnly>
                <Layout><ScoutingPlayerDetail /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/scouting/:rivalId/dashboard"
            element={
              <ProtectedRoute adminOnly>
                <Layout><RivalPlayerDashboard /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/fisioterapia"
            element={
              <ProtectedRoute adminOnly>
                <Layout><Physio /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/coaches"
            element={
              <ProtectedRoute adminOnly>
                <Layout><Coaches /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admins"
            element={
              <ProtectedRoute adminOnly>
                <Layout><Admins /></Layout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
